/**
 * Card "Google Calendar" para SettingsPage → Integraciones.
 *
 * Per-staff: el botón "Conectar" llama al endpoint `connect` que en modo mock
 * devuelve una `authUrl` placeholder y, sin navegar, llamamos al `callback`
 * para completar el OAuth fake. En la versión real reemplazaremos por
 * `window.location.href = authUrl`.
 *
 * Estado visible:
 *   - Cargando estado inicial.
 *   - Desconectado → botón "Conectar".
 *   - Conectado → fecha de conexión + botón "Desconectar".
 *   - Error → mensaje genérico.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@hooks/useAuth';
import { adminGoogleApi, type GoogleStatus } from '@api/client';

export function GoogleCalendarCard() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['google', 'status', 'user', userId];
  const { data, isLoading } = useQuery<GoogleStatus>({
    queryKey,
    queryFn: () => {
      if (!userId) throw new Error('NO_USER');
      return adminGoogleApi.status('user', userId);
    },
    enabled: userId !== null,
  });

  const connectMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('NO_USER');
      // Mock-only PR2: pedimos la authUrl pero NO navegamos. Completamos
      // el callback inmediatamente. En la versión real se reemplaza por:
      //   const { authUrl } = await adminGoogleApi.connect('user', userId);
      //   window.location.href = authUrl;
      await adminGoogleApi.connect('user', userId);
      await adminGoogleApi.completeMock('user', userId);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey }); setError(null); },
    onError: () => { setError('No se pudo conectar Google Calendar.'); },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('NO_USER');
      await adminGoogleApi.disconnect('user', userId);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey }); setError(null); },
    onError: () => { setError('No se pudo desconectar Google Calendar.'); },
  });

  const connected = data?.connected === true;
  const busy = connectMutation.isPending || disconnectMutation.isPending;

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Google Calendar</span>
        <span className="mono cell-muted" style={{ fontSize: 11 }}>MOCK</span>
      </div>
      <div style={{ padding: '14px 18px 18px' }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-muted)' }}>
          Sincroniza tus reservas con tu Google Calendar personal. Cuando una
          reserva tuya se crea, modifica o cancela, el evento se actualiza en
          tu calendario.
        </p>
        <p style={{ margin: '6px 0 14px', fontSize: 12, color: 'var(--fg-soft)' }}>
          <strong>Mock-only:</strong> esta versión no conecta a Google realmente — registra la
          operación localmente para validar el flow. La integración real es follow-up.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 13 }}>
            {isLoading && <span style={{ color: 'var(--fg-soft)' }}>Comprobando estado…</span>}
            {!isLoading && connected && (
              <span>
                <span className="pill" data-tone="ok" style={{ marginRight: 8 }}>Conectado</span>
                {data.connectedAt && (
                  <span className="mono" style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                    desde {new Date(data.connectedAt).toLocaleDateString('es-ES')}
                  </span>
                )}
              </span>
            )}
            {!isLoading && !connected && (
              <span className="pill" data-tone="neutral">No conectado</span>
            )}
          </div>
          <div>
            {connected ? (
              <button
                className="btn"
                data-variant="danger"
                disabled={busy}
                onClick={() => { disconnectMutation.mutate(); }}
              >
                {disconnectMutation.isPending ? 'Desconectando…' : 'Desconectar'}
              </button>
            ) : (
              <button
                className="btn"
                data-variant="primary"
                disabled={busy || userId === null}
                onClick={() => { connectMutation.mutate(); }}
              >
                {connectMutation.isPending ? 'Conectando…' : 'Conectar (mock)'}
              </button>
            )}
          </div>
        </div>
        {error && (
          <div style={{ marginTop: 10, padding: 8, background: 'var(--danger-soft)', borderRadius: 4, fontSize: 12, color: 'var(--danger)' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
